import ApiController, { ApiRequest } from "../controller";

class CustomerModule extends ApiController {
  resource = "";

  find(email) {
    const data = {
      email
    }

    const request = new ApiRequest({
      method: "POST",
      url: `/customer/find`,
      body: data
    });

    return this.request<[any]>(request);
  }

  info() {
    const request = new ApiRequest({
      method: "GET",
      url: `/info`
    });

    return this.request<any>(request);
  }

  upcomingReservations(page) {
    const request = new ApiRequest({
      method: "POST",
      url: `/customer/upcomingReservations`,
      body: { page }
    });

    return this.request<any>(request);
  }

  pastReservations(page) {
    const request = new ApiRequest({
      method: "POST",
      url: `/customer/pastReservations`,
      body: { page }
    });

    return this.request<any>(request);
  }

  reservation(id, date, customer?, signature?) {
    const request: any = {
      id,
      date
    }

    if (customer) request.customer = customer.id
    if (signature) request.signature = signature

    const req = new ApiRequest({
      method: "POST",
      url: `/customer/reservation`,
      body: request
    });

    return this.request<any>(req);
  }

  cancelReservation(
    id: string | number,
    date: string | number,
    type?: { id: string | number },
    note?: string
  ) {
    const request = {
      id,
      date,
      type: type?.id,
      note
    }

    const req = new ApiRequest({
      method: "POST",
      url: `/customer/reservationCancel`,
      body: request
    });

    return this.request<any>(req);
  }

  confirmAttendingReservation(id, date) {
    const request = {
      id,
      date
    }

    const req = new ApiRequest({
      method: "POST",
      url: `/customer/reservationConfirmAttending`,
      body: request
    });

    return this.request<any>(req);
  }

  reviewReservation(id, date, comment, rating, displayName) {
    const request = {
      id,
      date,
      comment,
      rating,
      displayName
    }

    const req = new ApiRequest({
      method: "POST",
      url: `/customer/reservationReview`,
      body: request
    });

    return this.request<any>(req);
  }

  orders(page) {
    const request = {
      page
    }

    const req = new ApiRequest({
      method: "POST",
      url: `/customer/orders`,
      body: request
    });

    return this.request<any>(req);
  }

  order(id) {
    const request = {
      id
    }

    const req = new ApiRequest({
      method: "POST",
      url: `/customer/order`,
      body: request
    });

    return this.request<any>(req);
  }

  orderPayment(id, action, amount, paymentData, paymentParams) {
    const payment: any = {}

    if (paymentData) {
      payment.data = paymentData
    }
    if (paymentParams) {
      payment.params = paymentParams
    }

    const request = {
      id,
      action,
      amount,
      payment
    }

    const req = new ApiRequest({
      method: "POST",
      url: `/customer/orderPayment`,
      body: request
    });

    return this.request<any>(req);
  }

  giftCards(page) {
    const request = {
      page
    }

    const req = new ApiRequest({
      method: "POST",
      url: `/customer/giftCards`,
      body: request
    });

    return this.request<any>(req);
  }

  passes(page) {
    const request = {
      page
    }

    const req = new ApiRequest({
      method: "POST",
      url: `/customer/passes`,
      body: request
    });

    return this.request<any>(req);
  }

  waitlists(page) {
    const request = {
      page
    }

    const req = new ApiRequest({
      method: "POST",
      url: `/customer/waitlists`,
      body: request
    });

    return this.request<any>(req);
  }

  waitlist(id, signature?) {
    const request: any = {
      id
    }

    if (signature) request.signature = signature

    const req = new ApiRequest({
      method: "POST",
      url: `/customer/waitlist`,
      body: request
    });

    return this.request<any>(req);
  }

  async waitlistAvailability(id) {
    const request = {
      id
    }

    const req = new ApiRequest({
      method: "POST",
      url: `/customer/waitlistAvailability`,
      body: request
    });

    const response = await this.request<any>(req);

    const services: Record<string, any> = {}
    const employees: Record<string, any> = {}

    if (response?.services) {
      for (let i = 0; i < response.services.length; i++) {
        const service = response?.services[i]
        services[service.id] = service
      }
    }

    if (response?.employees) {
      for (let i = 0; i < response.employees.length; i++) {
        const employee = response.employees[i]
        employees[employee.id] = employee
      }
    }

    if (response?.dates) {
      for (let i = 0; i < response.dates.length; i++) {
        const date = response.dates[i]

        for (let j = 0; j < date.slots.length; j++) {
          const slot = date.slots[j]

          for (let k = 0; k < slot.services.length; k++) {
            const slotService = slot.services[k]

            slotService.service = services[slotService.service.id]
            slotService.employee = employees[slotService.employee.id]
          }
        }
      }
    }

    return response;
  }

  waitlistCancel(id) {
    const request = {
      id
    }

    const req = new ApiRequest({
      method: "POST",
      url: `/customer/waitlistCancel`,
      body: request
    });

    return this.request<any>(req);
  }

  waitlistUpdate(id, dates) {
    const request = {
      id,
      dates
    }

    const req = new ApiRequest({
      method: "POST",
      url: `/customer/waitlistUpdate`,
      body: request
    });

    return this.request<any>(req);
  }

  formRequests(page) {
    const request = {
      page
    }

    const req = new ApiRequest({
      method: "POST",
      url: `/customer/formRequests`,
      body: request
    });

    return this.request<any>(req);
  }

  form(id) {
    const request = {
      id
    }

    const req = new ApiRequest({
      method: "POST",
      url: `/customer/form`,
      body: request
    });

    return this.request<any>(req);
  }

  submitForm(id, values, consentAccepted, sendEmail) {
    const request = {
      id,
      values,
      consentAccepted,
      sendEmail
    }

    const req = new ApiRequest({
      method: "POST",
      url: `/customer/submitForm`,
      body: request
    });

    return this.request<any>(req);
  }

  updateDetails(attributes, customFields) {
    const request = {
      attributes,
      customFields
    }

    const req = new ApiRequest({
      method: "POST",
      url: `/customer/updateDetails`,
      body: request
    });

    return this.request<any>(req);
  }

  photos(page, limit) {
    const request = {
      page,
      limit
    }

    const req = new ApiRequest({
      method: "POST",
      url: `/customer/photos`,
      body: request
    });

    return this.request<any>(req);
  }
}

export default CustomerModule;